import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { debug } from 'console';
import { promise } from 'protractor';
import { map } from 'rxjs/operators';
import { AppData } from 'src/app/app.data';
import { AppSettings } from 'src/app/app.settings';
import { Settings } from 'src/app/app.settings.model';
import { BlockLike } from 'typescript';
import { ApiResponseCode, EntityTypes } from '../enums';
import { Guid } from '../models/guid';
import { Utils } from '../utils';
import { FileUploadService } from './file-upload.service';

@Injectable({
  providedIn: 'root'
})
export class FileUpload2Service {

  //
  //   USE THIS SERVICE TO LOAD IMAGES
  //

  CUSTOMER_DOCUMENT_TYPE_DOCUMENT = 0;
  CUSTOMER_DOCUMENT_TYPE_IMAGE = 1;
  COMPRESS_MAX_WIDTH = 1000;
  COMPRESS_MAX_HEIGHT = 1000;

  settings: Settings;
  apiEndPoint: string = Utils.getApiEndPoint(); // environment.APIEndpoint;  
  
  files: any[] = [];      // files to upload 
  imgFileTypes = ["png", "jpg", "jpeg", "bmp", "svg", "tiff", "gif"];
  thumbWidth = 240;
  thumbHeight = 170;
  maxFileSize = 20971520; // 20mb default - will be updated by calling initPreference
  chunkSize = 524288; // 0.5mb   // will be updated by calling initPreference
  abortImageSave = false;
  error: string;
  autoCompressUploadedImages = false;
  isPreferenceLoaded = false;

  

  @Output() onProgress: EventEmitter<any> = new EventEmitter<any>();
  @Output() onComplete: EventEmitter<any> = new EventEmitter<any>();

  // for the document upload service
  private subscribeUploadComplete: any;
  private subscribeUploadProgress: any;

  constructor(private http: HttpClient,
    private appSettings: AppSettings,
    private appData: AppData,
    private translate: TranslateService,
    private uploadDocService: FileUploadService) {

    this.settings = appSettings.settings;
  }

  initPreference() {
    
    return this.http.get<any>(this.apiEndPoint + `UploadApi/GetUploadUserPreference`)
      .pipe(map(result => {
        this.chunkSize = result.chunkSize;
        this.maxFileSize = result.maxFileSize;
        this.autoCompressUploadedImages = result.autoCompressUploadedImages;
        this.COMPRESS_MAX_WIDTH = result.imageCompress_MaxWidth;
        this.COMPRESS_MAX_HEIGHT = result.imageCompress_MaxHeight;
        return result;
      }));
  }


  /**
   * 
   * @param event of file input change event
   * @param data any data to pass
   */
  async startUploadImage(event: any, customerID: number, isProfileImage: boolean) {
   
    var currentFiles = (event.currentTarget && event.currentTarget.files) ||
    (event.target && event.target.files) ||
    [];

    if(currentFiles.length === 0){
      this.onComplete.emit( { error: 'no files selected'});
      return;
    }



    let prms = this.getPrms();

    //
    // prepare data to register files to upload by chunks
    //
    if (currentFiles && currentFiles.length > 0) {
      for (let index = 0; index < currentFiles.length; index++) {        
        let file = currentFiles[index];
        this.files.push(file);        
        file.uid = file.uid || Guid.newGuid();
        file.prms = prms;
        file.innerParams = {
          fileName: file.name,
          entityType: EntityTypes.Customer,
          entityID: customerID,
          customerID: customerID,          
          uid: file.uid,
          isImage: false,
          ImageHeight: 0,
          ImageWidth: 0,
          isProfileImage: false
        };


        // read file to get base64Data
        var bufferArray = await this.getFileData(file);
        file.innerParams.rawBase64Data = bufferArray;
        file.innerParams.base64Data = bufferArray.toString().split(',')[1];


        // try to compress the image file if needed
        var imgElement = await this.checkIfImage(file.innerParams.rawBase64Data);
        if (!imgElement) {
          var msg = this.translate.instant("ERROR.INVALID-IMAGE-FILE-MSG");
          this.onComplete.emit({ error: msg });
          return;
        }


        file.innerParams.isImage = true;
        file.innerParams.isProfileImage = isProfileImage;
        file.innerParams.ImageWidth = imgElement.width;
        file.innerParams.ImageHeight = imgElement.height;

        
        
        // try to compress the image file if needed
        if (this.autoCompressUploadedImages) {
          this.tryToCompressImage(file).then(compressedFile => {
            this.startUpload(compressedFile);
          }, reason => {            
            // rejected
            this.error = reason;
          });
          
        }
        else{
          this.startUpload(file);
        }
        
        
      }
    }

  }

  startUploadDocument(event: any, customerID: number, departmentID: number, entityID: number, entityType: EntityTypes) {
    
    if (this.subscribeUploadComplete) {
      this.subscribeUploadComplete.unsubscribe();
    }
    if (this.subscribeUploadProgress) {
      this.subscribeUploadProgress.unsubscribe();
    }

    this.subscribeUploadProgress = this.uploadDocService.onProgress.subscribe(result => {          
      this.onProgress.emit(result);
    });

    this.subscribeUploadComplete = this.uploadDocService.onComplete.subscribe(result => {      
      this.onComplete.emit(result);
    });

    this.uploadDocService.startUploadDocument(event, customerID, departmentID, entityID, entityType);

  }

  private getPrms() {
    return {
      chunkSize: this.chunkSize,
      compressImage: this.autoCompressUploadedImages,
      maxWidth: this.COMPRESS_MAX_WIDTH,
      maxHeight: this.COMPRESS_MAX_HEIGHT
    };
  }



  private checkIfImage(rawBase64Data): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      var image = new Image();
      image.onload = () => {
        resolve(image);
      };
      image.onerror = () => {
        resolve(null);
      }
      image.src = rawBase64Data;
    });
  }

  private getFileData(file: any): Promise<any> {
    return new Promise((resolve, reject) => {
      var reader = new FileReader();
      reader.onloadend = function () {
        resolve(this.result);
      };
      reader.readAsDataURL(file);
    });
  }



  private tryToCompressImage(file: any): Promise<any> {
    return new Promise((resolve, reject) => {
      var image = new Image();
      image.onload = () => {
        this.compressImage(file, image);
        resolve(file);
      };
      image.onerror = (err) => {        
        reject(err);   
        file.error = err;    
      };


      //Set the Base64 string return from FileReader as source.
      image.src = file.innerParams.rawBase64Data;

    });
  }


  private compressImage(file: any, image: any) {

    var canvas = document.createElement('canvas'),
      width = image.width,
      height = image.height;

    var factorX = (file.prms.maxWidth ? file.prms.maxWidth : width) / width;
    var factorY = (file.prms.maxHeight ? file.prms.maxHeight : height) / height;
    var factor = Math.min(factorX, factorY);

    width = (width * factor).toFixed(0);
    height = (height * factor).toFixed(0);

    if (!file.prms.compressImage || factor >= 1 || factor === 0) {
      return image.src;
    }


    file.innerParams.ImageHeight = height;
    file.innerParams.ImageWidth = width;

    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(image, 0, 0, width, height);

    var dataUrl = canvas.toDataURL('image/jpeg');
    file.innerParams.rawBase64Data = dataUrl;
    file.innerParams.base64Data = dataUrl.split(",")[1]; // get base64 data after "data:image/png;base64,"   
  }

  private startUpload(file: any) {

    var fileData = file.innerParams.base64Data;
    file.innerParams.fileSize = fileData.length / 2; // divide by two to get the size from base64

    var registerData = {
      Name: file.innerParams.fileName,
      EntityTypeInt: file.innerParams.entityType,
      EntityID: file.innerParams.entityID,
      CustomerID: file.innerParams.customerID,
      DepartmentID: file.departmentID,
      Active: true,
      ImageWidth: file.innerParams.ImageWidth,
      ImageHeight: file.innerParams.ImageHeight,
      Size: file.innerParams.fileSize,
      FileUID: file.innerParams.uid || Guid.newGuid(),
      IsProfileImage: file.innerParams.isProfileImage || false
    };



    file.fileDataLength = fileData.length;
    file.fileDataShift = 0;
    file.fileDataChunkSize = file.prms.chunkSize;
    file.chunkID = 0;
    file.dataForSend = null;
    file.registerData = registerData;

    // register
    this.regsiterUploadData(file).subscribe(result => {
      let guid = result.Data;
      var currentFile = Array.from(this.files).filter(x => x.uid === guid)[0];
      if (result.Code == ApiResponseCode.Success) {
        this.uploadFileByChunk(currentFile);
      }
      else {

        var filesData = currentFile.registerData;
        console.log('failed registered files' + JSON.stringify(filesData));

        this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
        this.onComplete.emit({ action: 'register', error: this.error, fileUID: guid  });
      }
    }, error => {
      this.error = error;
      this.onComplete.emit({ action: 'register', error: error, fileUID: registerData.FileUID });
    })

  }





  private regsiterUploadData(file: any) {
    var data = file.registerData;
    console.log('RegisterUploadByChunks ' + file.innerParams.fileName);
    return this.http.post<any>(this.apiEndPoint + `UploadApi/RegisterUploadByChunks`, JSON.stringify(data))
      .pipe(map(result => {
        return result;
      }));
  }

  private uploadFileByChunk(file: any) {

    var progress = Math.round(file.fileDataShift / file.fileDataLength * 100);
    file.innerParams.percentComplete = progress;
    this.onProgress.emit({ progress: progress, fileUID:file.uid  });

    var lastChunk = false;
    var dataForSend = null;

    // is last chunk
    if (file.fileDataShift >= file.fileDataLength) {
      this.finishUploadByChunk(file.uid).subscribe(result => {        
        var guid = result.Data.FileUID;
        var currentFile = Array.from(this.files).filter(x => x.uid === guid)[0];
        currentFile.completed = true;
        progress = Math.round(currentFile.fileDataShift / currentFile.fileDataLength * 100);
        this.onProgress.emit({ progress: progress });
        this.onComplete.emit({});
      });
      return;
    }



    // continue upload by chunks                
    if (file.fileDataLength - file.fileDataShift > file.fileDataChunkSize) {

      dataForSend = file.innerParams.base64Data.slice(file.fileDataShift, file.fileDataShift + file.fileDataChunkSize);
      file.fileDataShift += file.fileDataChunkSize;
    }
    else { // send last bytes    

      dataForSend = file.innerParams.base64Data.slice(file.fileDataShift);
      file.fileDataShift = file.fileDataLength;
      lastChunk = true;
    }


    ++file.chunkID;
    var packet = {
      ChunkID: file.chunkID,
      Guid: file.registerData.FileUID,
      Data: dataForSend,
      LastChunk: lastChunk,
      fileName: file.innerParams.fileName
    };

    this.uploadChunk(packet).subscribe(result => {

      var guid = result.Data.Guid;
      var file = Array.from(this.files).filter(f => f.uid === guid)[0];
      if (result.Code === ApiResponseCode.Success) {
        this.uploadFileByChunk(file); // called recursively
      }
      else {
        this.error = result.Error;
        this.onComplete.emit({ error: this.error });

      }

    }, error => {

      this.error = error;
      this.onComplete.emit({ error: this.error });
    });
  }

  private uploadChunk(packet) {
    console.log('upload chunk: ' + packet.fileName + ' ' + packet.Guid);
    return this.http.post<any>(this.apiEndPoint + `UploadApi/UploadChunk`, JSON.stringify(packet))
      .pipe(map(result => {
        return result;
      }));
  }

  private finishUploadByChunk(fileUID) {    
    const file = Array.from(this.files).filter(x=> x.uid === fileUID)[0]
    console.log('post finish file uid: ' + file.innerParams.fileName + ' ' + fileUID);
    return this.http.get<any>(this.apiEndPoint + `UploadApi/FinishUploadChunk/${fileUID}`)
      .pipe(map(result => {
        return result;
      }));
  }








}
